import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SectionHeading from '@/components/feature/SectionHeading';
import ArticleRow from '@/components/feature/ArticleRow';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories, newsList } from '@/mocks/news';
import { useSEO } from '@/hooks/useSEO';
import HeroCarousel from './components/HeroCarousel';
import CategoryGrid from './components/CategoryGrid';
import HotRanking from './components/HotRanking';

export default function Home() {
  useSEO({
    title: '911爆料网 - 最新社会热点与爆料新闻平台',
    description:
      '911爆料网为您提供最新最全的社会热点爆料与新闻资讯，涵盖民生事件、突发新闻等内容，帮助用户及时获取第一手信息与多角度解析。',
    keywords: '911爆料网,爆料新闻,社会热点,最新资讯,突发事件,民生新闻,新闻平台',
  });

  const sorted = [...newsList].sort(
    (a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
  );
  const focus = sorted.slice(0, 5);
  const latest = sorted.slice(0, 8);
  const hot = [...newsList].sort((a, b) => b.views - a.views).slice(0, 7);
  const videos = newsList.filter((n) => n.category === 'video').slice(0, 3);
  const investigations = newsList.filter((n) => n.category === 'investigation').slice(0, 3);

  const counts = categories.reduce<Record<string, number>>((acc, c) => {
    acc[c.id] = newsList.filter((n) => n.category === c.id).length;
    return acc;
  }, {});

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
        <h1 className="sr-only">911爆料网 - 最新社会热点与爆料新闻平台</h1>

        <HeroCarousel articles={focus} />

        <section className="mt-8 md:mt-10">
          <SectionHeading title="热门栏目" icon="ri-apps-2-line" moreTo="/category/society" />
          <CategoryGrid counts={counts} />
        </section>

        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <section className="lg:col-span-2">
            <SectionHeading title="最新爆料" icon="ri-flashlight-line" moreTo="/category/breaking" />
            <div className="rounded-lg border border-background-200 bg-background-50 px-4 md:px-5">
              {latest.map((article) => (
                <ArticleRow key={article.id} article={article} />
              ))}
            </div>
          </section>

          <aside className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <SectionHeading title="热门爆料榜" icon="ri-fire-line" />
              <HotRanking articles={hot} />
            </section>

            <section className="overflow-hidden rounded-lg bg-primary-900 p-5 text-background-100">
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-500 text-primary-950">
                <i className="ri-megaphone-line text-2xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold text-background-50">
                有料你就来
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-background-300">
                城市的角落、身边的瞬间，都值得被记录。你的每一条线索，都可能成为别人需要的真相。
              </p>
              <Link
                to="/submit"
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-send-plane-line"></i>
                立即投稿爆料
              </Link>
            </section>
          </aside>
        </div>

        <section className="mt-10 md:mt-12">
          <SectionHeading title="深度调查" icon="ri-search-eye-line" moreTo="/category/investigation" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {investigations.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </section>

        <section className="mt-10 md:mt-12">
          <SectionHeading title="视频爆料" icon="ri-live-line" moreTo="/category/video" />
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {videos.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </section>

        <section className="relative mt-10 overflow-hidden rounded-lg md:mt-12">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20artistic%20city%20skyline%20at%20dusk%20with%20long%20exposure%20light%20trails%20and%20deep%20blue%20tone%2C%20minimal%20modern%20editorial%20background%2C%20soft%20gradient%20sky%2C%20high%20detail%2C%20harmonious%20composition&width=1600&height=600&seq=home-cta-band&orientation=landscape"
            alt="911爆料网投稿通道"
            title="投稿爆料 - 911爆料网"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-950/80"></div>
          <div className="relative flex flex-col items-start gap-5 p-6 md:flex-row md:items-center md:justify-between md:p-10">
            <div className="max-w-2xl">
              <h2 className="font-heading text-xl font-extrabold text-background-50 md:text-2xl">
                看见问题，也是一种力量
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-background-200 md:text-base">
                911爆料网致力于让每一条民生线索被认真对待。欢迎提供图文、视频线索，我们将持续跟进，还原事件全貌。
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-3">
              <Link
                to="/submit"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-edit-box-line"></i>
                我要爆料
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200/50 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
              >
                了解我们
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}